import React from 'react';
import { IonIcon, IonTextarea } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { UPDATE_SUMMARY_MAX_LENGTH } from '@/modules/config';
import styles from './UpdateSummaryField.module.css';

/**
 * The sender's summary of what changed, which an update's body carries as its
 * `{_updateSummary}` primitive (#427): a prompt, the text itself with a running
 * count, and a clear button while there is text. The text is live as it is
 * typed — previews and sends read it straight away, so there is nothing to
 * confirm.
 *
 * The clear button is styled to match the cancel control of the compose Field
 * Options "Other" input (OtherInputAction in compose/FieldEditor.tsx) — an
 * outlined round neutral-medium button — mirrored here rather than imported so
 * this field doesn't pull in the compose/template dependency tree. It
 * preventDefaults the press so a focused textarea keeps focus and the user can
 * type a replacement straight away.
 *
 * The wizard's Changes step docks it above its Next button; the garden
 * composer puts it at the top of the Form tab. Both hold the text in the
 * sharing session, so either one writing it is what the other reads.
 */
const UpdateSummaryField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  /** The textarea's test id; the clear button suffixes it. */
  testId: string;
}> = ({ value, onChange, testId }) => {
  return (
    <>
      <p className={styles.prompt}>
        Summarize the changes in a sentence or two
      </p>
      <div className={styles.box}>
        <IonTextarea
          value={value}
          onIonInput={e => onChange(e.detail.value ?? '')}
          maxlength={UPDATE_SUMMARY_MAX_LENGTH}
          rows={4}
          placeholder="e.g. The venue moved to Grant Park and we now start at noon."
          aria-label="Update summary"
          data-testid={testId}
          className={styles.textarea}
        />
        <div className={styles.footer}>
          <span className={styles.counter}>
            {value.length} / {UPDATE_SUMMARY_MAX_LENGTH}
          </span>
          {value.length > 0 && (
            <button
              type="button"
              data-testid={`${testId}-clear`}
              aria-label="Clear summary"
              onMouseDown={e => e.preventDefault()}
              onPointerDown={e => e.preventDefault()}
              onClick={e => {
                e.stopPropagation();
                onChange('');
              }}
              className={styles.clear}
            >
              <IonIcon icon={closeOutline} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateSummaryField;
